<template>
  <div class="flex w-full flex-col gap-6">
    <div
      v-for="section in items.filter(section => (section.condition ? section.condition() : true))"
      :key="section.id"
      class="flex flex-col gap-0.5"
    >
      <div v-if="!section.isHidden" :id="section.id" class="text-navigation-header p-2 text-black-60">
        {{ section.text }}
      </div>
      <div
        v-for="group in section.items.filter(group => (group.condition ? group.condition() : true))"
        :key="group.id"
        class="rounded-lg transition-all duration-200 hover:bg-black-10"
        :class="{
          'bg-secondary-05': modelValue === group.id,
        }"
      >
        <div
          v-if="!group.isHidden"
          :id="disableScrollBehavior ? '' : group.id"
          class="text-subhead-4 flex cursor-pointer flex-row items-center gap-2 p-2 transition-all duration-200 text-black-70"
          :class="{
            'rounded-lg bg-black-100 text-white': modelValue === group.id,
          }"
          @click="onItemClick(group)"
        >
          <UIIcon
            v-if="group.icon"
            :name="group.icon"
            size="sm"
            :class="[{ '!text-error-100': group.error }, group.iconClass]"
          />
          <UIIcon
            v-else-if="group.items?.length"
            name="chevron-big-filled-down"
            size="sm"
            :class="[{ 'rotate-180': openPanels[group.id] }, iconClass, 'transition-all duration-200']"
          />
          <span :class="{ '!text-error-100': group.error }">
            {{ group.text }}
          </span>
        </div>
        <UIExpansionPanel
          v-for="subGroup in group.items?.filter(
            subGroup => (subGroup.condition ? subGroup.condition() : true) && subGroup.items,
          )"
          :id="disableScrollBehavior ? '' : subGroup.id"
          :key="subGroup.id"
          v-model="openPanels[subGroup.id]"
          class="flex flex-col gap-0.5 [&>label]:!mb-0"
          :title="subGroup.text"
        >
          <template #title>
            <div class="flex w-full items-center justify-between">
              <div class="text-subhead-4 flex h-9 cursor-pointer flex-row items-center gap-2 p-2">
                <UIIcon
                  v-if="subGroup.icon"
                  :name="subGroup.icon"
                  size="sm"
                  :class="[{ '!text-error-100': subGroup.error }, subGroup.iconClass, 'text-black-70']"
                />
                <span class="text-black-70">
                  {{ subGroup.text }}
                </span>
              </div>
              <UIIcon
                v-if="subGroup.chevronAfter"
                name="chevron-big-filled-down"
                :class="[{ 'rotate-180': openPanels[subGroup.id] }, 'transition-all duration-200 text-black-70']"
              />
            </div>
          </template>
          <template #default>
            <div
              v-for="item in subGroup.items?.filter(item => (item.condition ? item.condition() : true))"
              :key="item.id"
              class="rounded-lg"
            >
              <UITooltip placement="bottom-start">
                <div
                  :id="disableScrollBehavior ? '' : item.id"
                  class="text-subhead-4 flex h-9 cursor-pointer flex-row items-center gap-2 p-2 pl-7 transition-all duration-200 text-black-70"
                  :class="{ 'rounded-lg bg-black-100 text-white': modelValue === item.id }"
                  @click="onItemClick(item)"
                >
                  <span class="text-3xl"> ᐧ </span>
                  <p class="line-clamp-1">
                    {{ item.text }}
                  </p>
                  <div v-if="item.suffix" class="text-caption-2 rounded-full bg-dm-80 px-2 py-1 text-white">
                    {{ item.suffix }}
                  </div>
                </div>
                <template v-if="item.text.length > 20" #content>
                  {{ item.text }}
                </template>
              </UITooltip>
            </div>
          </template>
        </UIExpansionPanel>
        <div
          v-for="item in group.items?.filter(
            subGroup => (subGroup.condition ? subGroup.condition() : true) && !subGroup.items,
          )"
          :id="disableScrollBehavior ? '' : item.id"
          :key="item.id"
          class="rounded-lg"
        >
          <div
            class="text-subhead-4 flex h-9 cursor-pointer flex-row items-center gap-2 p-2 pl-7 transition-all duration-200 text-black-70"
            :class="{ 'rounded-lg bg-black-100 text-white': modelValue === item.id }"
            @click="onItemClick(item)"
          >
            <span class="text-3xl"> ᐧ </span>
            <span>
              {{ item.text }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { VerticalNavigationMenuSection, VerticalNavigationMenuItem } from '~/types/ui'

const route = useRoute()

const emits = defineEmits(['update:modelValue'])

type Props = {
  items: VerticalNavigationMenuSection[]
  disableScrollBehavior?: boolean
}
const props = defineProps<Props>()

const modelValue = defineModel<string>({ required: true })

const onItemClick = (item: VerticalNavigationMenuItem) => {
  emits('update:modelValue', item.id)

  if (item.url) {
    navigateTo(item.url)
  }
}

// reactive map with open or close expansion panels
const openPanels = ref<Record<string, boolean>[]>(
  props.items
    .map(section => section.items)
    .flat()
    .map(item => [...(item.items || []), item])
    .filter(Boolean)
    .flat()
    .map(item => ({
      [item.id]: false,
    })),
)

// open expansion panel of current tab
if (modelValue.value) {
  const parent = props.items
    .map(section => section.items)
    .flat()
    .map(item => [...(item.items || []), item])
    .filter(Boolean)
    .flat()
    .find(item => item?.items?.some(i => i.id === modelValue.value))

  if (parent) openPanels.value[parent.id] = true
}

watchEffect(() => {
  const item = props.items
    .map(section => section.items)
    .flat()
    .map(item => [...(item.items || []), item])
    .filter(Boolean)
    .flat()
    .map(item => [...(item?.items || []), item])
    .filter(Boolean)
    .flat()
    .find(item => item?.url === route.path)

  if (item) {
    emits('update:modelValue', item.id)
  }
})
</script>

<style scoped></style>
